<template>
  <layout-default
    :floatingButton="!loading && !directory.locked && !currentFilter"
    @floating-button-action="openNewFileDialog"
  >
    <template v-slot:toolbar>
      <library-filters
        :value="currentSearch ? currentSearch : ''"
        :currentFilter="currentFilter"
        @search-on:clear="searchClear"
        @search-on:input="searchInput"
        @search-on:run="searchRun"
        @filter="filterBy"
      />
    </template>
    <div class="library">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <library-breadcrumbs
        :search="currentSearch"
        :breadcrumbs="breadcrumbsFormatted"
        @load-directory="loadDirectory"
        @clear-search="searchClear"
        @refresh-results="filterBy({ key: 'name', value: currentSearch })"
      ></library-breadcrumbs>
      <h5
        class="library-heading"
        v-if="folders.length > 0 || (!directory.locked && !currentFilter)"
      >
        Folders
      </h5>
      <div class="library-folders">
        <div
          class="library-folders__new"
          v-if="!directory.locked && !currentFilter"
          @click="openNewFolderDialog"
        >
          <v-icon>bhi-plus</v-icon>
          Add folder
        </div>
        <library-folder
          v-for="folder in folders"
          :key="folder.id"
          :id="folder.id"
          :name="folder.name"
          :count="folder.recursiveFileCount"
          :version="folder.version"
          :locked="folder.locked"
          :modalDirectory="modalDirectory"
          @load-directory="loadDirectory"
          @rename="openRenameFolderDialog"
          @delete="openConfirmDeleteDialog(folder.id, folder.version, 'folder')"
          @share="openChoosePracticeDialog"
          @loadModalDirectory="loadModalDirectory"
          @move="moveFolder"
          @copy="copyFolder"
          @deleteFolderContent="deleteContent"
        />
      </div>
      <h5
        class="library-heading"
        v-if="files.length > 0 || (!directory.locked && !currentFilter)"
      >
        Files
      </h5>
      <div class="library-files">
        <div
          class="library-files__new"
          v-if="!directory.locked && !currentFilter"
          @click="openNewFileDialog"
        >
          <v-icon>bhi-plus</v-icon>
          <span>Add new file</span>
        </div>
        <library-file
          v-for="file in files"
          :key="file.id"
          :id="file.id"
          :name="file.title ? file.title : file.name"
          :thumbnailUrl="file.thumbnailUrl"
          :creationDate="file.creationDate"
          :createdById="file.createdById"
          :version="file.version"
          :favoriteProfileId="file.favoriteProfileId"
          :locked="file.locked"
          :editable="true"
          :modalDirectory="modalDirectory"
          :fileTypeId="file.fileTypeId"
          :profileId="profileId"
          :message-resource="resource ? resource : false"
          :get-resource="getResource"
          :selected="file.selected"
          :labels="file.labels"
          :isLoading="isLoading"
          @select-file="selectFile"
          :hoverLoader="progressBarsValues[file.id]"
          :processing="file.processing"
          :showScheduleMessageDialog="true"
          :showCreateExercise="true"
          :showCreateActivity="true"
          :showSendMessage="true"
          @openCreateExerciseDialog="openCreateExerciseDialog(file)"
          @openCreateActivityDialog="openCreateActivityDialog(file)"
          @openScheduleMessageDialog="openScheduleMessageDialog(file.id, files)"
          @openSendMessageDialog="openSendMessageDialog(file)"
          @mouseover.native="
            fileOpenHover(file.id, file.thumbnailUrl, file.previewUrl)
          "
          @mouseleave.native="fileClearHover(file.id)"
          @delete="openConfirmDeleteDialog(file.id, file.version, 'file')"
          @rename="openRenameFileDialog"
          @share="openChoosePracticeDialog"
          @download-file="downloadFileHandler(true, file.id)"
          @toggleFavorite="favoriteResource"
          @toggleTag="toggleTag"
          @loadModalDirectory="loadModalDirectory"
          @show-bigger-image="showBiggerImage"
          @stopHoverPopup="
            fileClearHover(file.id)
            fileCloseHover()
          "
          @move="moveFile"
          @copy="copyFile"
          @changeThumbnail="openSetThumbnailDialog"
        />
      </div>
      <div class="library-pagination--container">
        <v-pagination
          v-if="pagination.totalPages > 1"
          circle
          :length="pagination.totalPages"
          :value="pagination.pageNumber + 1"
          class="library-pagination"
          @input="changeLibraryPage"
        />
      </div>
      <h5
        class="library-heading"
        v-if="currentFilter && folders.length == 0 && files.length == 0"
      >
        Nothing found, clear filters
      </h5>
      <new-file-dialog
        :key="newFileDialogKey"
        :show-dialog="showNewFileDialog"
        :get-mime-types="getMimeTypes"
        :show-notify-check="currentDirectoryIsParticipantDir"
        @close-dialog="closeNewFileDialog"
        @on-change="uploadNewFile"
        @on-submit="addNewFile"
      />
      <preview-zoom-dialog
        :dialogType="dialogType"
        :imageSrc="imageSrc"
        :dialogFile="dialogFile"
        :dialogFileMlUrl="dialogFileMlUrl"
        :name="imageName"
        @close-dialog="closeMediaDialog"
        @reload-resource="reloadImageResource"
        :messageText="sendMessageText"
        @add-message="sendMessageAdd"
        @change-message="sendMessageEdit"
      />
      <choose-practice-dialog
        :key="choosePracticeDialogKey"
        :show-dialog="showChoosePracticeDialog"
        singleChoose
        @close-dialog="closeChoosePracticeDialog"
        @on-submit="choosePractice"
      />
      <v-dialog
        v-model="fileHover"
        hide-overlay
        attach=".library"
        content-class="file-hover-dialog"
        @click:outside="onClickOutsideFileHover"
        @keydown="onKeydownFileHover"
      >
        <v-card @mouseleave.native.stop="fileCloseHover()">
          <img
            v-if="fileHoverUrl && fileHoverType == 'base64'"
            :src="'data:image/jpeg;base64,' + fileHoverUrl"
          />
          <img
            v-else-if="fileHoverUrl && fileHoverType == 'url'"
            :src="fileHoverUrl"
          />
          <v-btn color="white" icon size="24" @click="fileCloseHover">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showNewFolderDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideNewFolder"
        @keydown="onKeydownNewFolder"
      >
        <v-card>
          <v-card-title class="headline">Enter folder name</v-card-title>
          <v-card-text>
            <v-text-field
              label="Folder name"
              v-model="newFolderName"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeNewFolderDialog"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              :disabled="!newFolderName"
              color="accent"
              @click="confirmNewFolder"
              >Create</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showConfirmDeleteDialog"
        persistent
        max-width="500"
        @click:outside="onClickOutsideDeleteFile"
        @keydown="onKeydownDeleteFile"
      >
        <v-card>
          <v-card-title class="headline"
            >Are you sure you want to delete this item? 🥵</v-card-title
          >
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeDialogs()
                clearEditFileInfo()
                clearEditFolderInfo()
              "
              >No</v-btn
            >
            <v-btn
              v-if="forceDeletionAllowed"
              :loading="forceDeletionInProgress"
              class="rounded-pill elevation-0"
              color="accent"
              @click="
                itemType == 'folder'
                  ? openDeleteFolderDialog()
                  : openDeleteFileDialog()
              "
              >Yes</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showDeleteFileDialog"
        persistent
        max-width="500"
        @click:outside="onClickOutsideDeleteFile"
        @keydown="onKeydownDeleteFile"
      >
        <v-card>
          <v-card-title class="headline" v-if="!forceDeletionText"
            >Are you really sure you want to delete this for
            EVERYONE?</v-card-title
          >
          <v-card-title v-else
            >The following resources are / were used in Timed Release, review
            the list and confirm your action</v-card-title
          >
          <v-card-text
            v-if="forceDeletionText"
            v-html="forceDeletionText"
          ></v-card-text>
          <v-card-actions class="v-card__actions--flexcenter">
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeDialogs()
                clearEditFileInfo()
                clearEditFolderInfo()
              "
              >{{ forceDeletionAllowed ? 'Cancel' : 'Close' }}</v-btn
            >
            <v-btn
              v-if="forceDeletionAllowed"
              :loading="forceDeletionInProgress"
              class="rounded-pill elevation-0"
              color="accent"
              @click="deleteFileHandler"
              >{{ forceDeletionText ? 'Confirm' : 'Remove' }}</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showRenameFileDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideReneameFile"
        @keydown="onKeydownReneameFile"
      >
        <v-card>
          <v-card-title class="headline">Renaming file</v-card-title>
          <v-card-text>
            <v-text-field
              label="New file title"
              v-model="changeFileName"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeDialogs()
                clearEditFileInfo()
              "
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              :disabled="!changeFileName || changeFileName == editFileName"
              @click="renameFile"
              >Rename</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showDeleteFolderDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideDeleteFolder"
        @keydown="onKeydownDeleteFolder"
      >
        <v-card>
          <v-card-title class="headline"
            >Are you really sure you want to delete this for
            EVERYONE?</v-card-title
          >
          <v-card-text
            >All files and folders inside of it will be also
            removed.</v-card-text
          >
          <v-card-actions class="v-card__actions--flexcenter">
            <v-btn
              class="rounded-pill"
              text
              @click="
                closeDialogs()
                clearEditFileInfo()
              "
              >{{ forceDeletionAllowed ? 'Cancel' : 'Close' }}</v-btn
            >
            <v-btn
              v-if="forceDeletionAllowed"
              class="rounded-pill elevation-0"
              color="accent"
              @click="deleteFolderHandler"
              >Remove</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <schedule-message-dialog
        :showDialog="showScheduleMessageDialog"
        :messageScheduled="messageScheduled"
        :newMessageData="newMessageData"
        :scheduleMessageResource="scheduleMessageResource"
        @open-schedule-message-dialog="openScheduleMessageDialog"
        @open-choose-participant-dialog="
          openChooseParticipantDialog('scheduleMessage')
        "
        @show-bigger-image="showBiggerImage"
        @close-schedule-message-dialog="closeScheduleMessageDialog"
      />
      <participant-list-dialog
        :showDialog="showChooseParticipantDialog"
        :submitMessageText="participantsListSubmitText"
        :title="participantsListTitle"
        :return-value="participantsListReturnValue"
        @close-dialog="closeChooseParticipantDialog"
        @submit-message="onSubmitParticipantsList"
      />
      <v-dialog
        v-model="showRenameFolderDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideRenameFolder"
        @keydown="onKeydownRenameFolder"
      >
        <v-card>
          <v-card-title class="headline">Rename folder</v-card-title>
          <v-card-text>
            <v-text-field
              label="Folder name"
              v-model="newFolderName"
              required
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeDialogs"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              :disabled="!newFolderName || newFolderName == editFolderName"
              @click="renameFolder"
              >Rename</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog
        v-model="showSetThumbnailDialog"
        persistent
        max-width="290"
        @click:outside="onClickOutsideSetThumbnail"
        @keydown="onKeydownSetThumbnail"
      >
        <v-card>
          <v-card-title class="headline">Upload new thumbnail</v-card-title>
          <v-card-text>
            <thumbnail-picker
              :key="thumbnailPickerKey"
              label=""
              :current-file="newThumbnailFile"
              :current-mobile-static-media="newThumbnailStaticMedia"
              :enable-mobile-static-media="true"
              :force-mobile-static-media="false"
              :readonly="false"
              :allowed-file-types="[18]"
              :one-source-only="true"
              @thumbnail-picked="setThumbnailFile"
              @refresh-current-file="reloadThumbnailResource"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn class="rounded-pill" text @click="closeDialogs"
              >Cancel</v-btn
            >
            <v-btn
              class="rounded-pill elevation-0"
              color="accent"
              :disabled="!newThumbnailSelected"
              @click="changeFileThumbnail"
              >Save</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
      <ml-file-picker-dialog
        :show-dialog="showMlFilePickerDialog"
        @close-dialog="closeMlFilePickerDialog"
        @file-picked="chosenFileMlFilePickerDialog"
        :allowedFileTypes="customMediaThumbnailAllowedFileTypes"
        v-on="$listeners"
      />
      <static-media-picker-dialog
        :show-dialog="showStaticMediaPicker"
        @close-dialog="closeStaticMediaPicker"
        @static-media-picked="chosenStaticMediaPicker"
      />
      <exercise-definition-dialog
        :show-dialog="showEditExerciseDialog"
        :key="newExerciseDialogKey"
        :add-alternatives="false"
        :enabled-notes="true"
        :participants-exercises="true"
        :get-resource="getResource"
        :get-mime-types="getMimeTypes"
        :exercise-resource="exerciseResource ? exerciseResource : false"
        :exercise-resource-additional="
          exerciseResourceAdditional ? exerciseResourceAdditional : false
        "
        :edited-exercise="exerciseDefinition"
        :exercise-definitions="exerciseSingleDefinitions"
        :add-to-series="true"
        :parameters-enabled="true"
        :initial-mode="exerciseDialogMode"
        :disable-cancel="exerciseDialogDisableCancel"
        :hide-prev="exerciseDialogHidePrev"
        :show-add-to-collection="true"
        :readonly-media="true"
        :show-additional-media="false"
        @close-exercise-dialog="
          showEditExerciseDialog = false
          newExerciseDialogKey++
        "
        @show-bigger-image="showBiggerImage"
        @on-submit="createNewExerciseDefinition"
        @reload-resource="reloadExerciseResource"
        @reload-exercise-resource="reloadExerciseResource"
        @reload-exercise-resource-additional="
          reloadExerciseResource($event, 'additionalMedia')
        "
        @clear-resource="clearExerciseResource"
        @clear-resource-additional="clearExerciseResourceAdditional"
        @reload-exercises="reloadExerciseDefinitions"
        @on-series-search="loadDialogExerciseDefinitions"
        v-on="$listeners"
      />
    </div>
  </layout-default>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import LayoutDefault from '@/layouts/LayoutDefault.vue'
import LibraryFilters from '@/components/library/LibraryFilters.vue'
import LibraryFolder from '@/components/library/LibraryFolder.vue'
import LibraryFile from '@/components/library/LibraryFile.vue'
import LibraryBreadcrumbs from '@/components/library/LibraryBreadcrumbs.vue'
import NewFileDialog from '@/components/dialogs/NewFileDialog.vue'
import PreviewZoomDialog from '@/components/dialogs/PreviewZoomDialog.vue'
import ChoosePracticeDialog from '@/components/dialogs/ChoosePracticeDialog.vue'
// import DialogFile from '@/components/forms/DialogFileInput.vue'
import { debounce } from '@/helpers/debounce'
import StaticMediaPickerDialog from '@/components/dialogs/StaticMediaPickerDialog.vue'
import MlFilePickerDialog from '@/components/dialogs/MlFilePickerDialog.vue'
import ParticipantListDialog from '@/components/dialogs/ParticipantListDialog.vue'
import ScheduleMessageDialog from '@/components/dialogs/ScheduleMessageDialog.vue'
import ExerciseDefinitionDialog from '@/components/dialogs/ExerciseDefinitionDialog.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'
import GlobalUploadMixin from '@/mixins/globalUpload'
import isStringBase64 from '@/helpers/isStringBase64.js'
import ThumbnailPicker from '@/components/inputs/ThumbnailPicker.vue'

export default {
  name: 'Library',
  components: {
    LayoutDefault,
    LibraryFilters,
    LibraryFolder,
    LibraryFile,
    LibraryBreadcrumbs,
    NewFileDialog,
    PreviewZoomDialog,
    ChoosePracticeDialog,
    // DialogFile,
    StaticMediaPickerDialog,
    MlFilePickerDialog,
    ParticipantListDialog,
    ScheduleMessageDialog,
    ExerciseDefinitionDialog,
    ThumbnailPicker,
  },
  mixins: [TenantTextsMixin, GlobalUploadMixin],
  data() {
    return {
      showNewFileDialog: false,
      searchBy: {
        name: '',
      },
      newFile: null,
      showFileDialog: false,
      currentDirectoryId: null,
      showNewFolderDialog: false,
      newFolderName: '',
      changeFileName: '',
      showConfirmDeleteDialog: false,
      showDeleteFileDialog: false,
      showRenameFileDialog: false,
      showDeleteFolderDialog: false,
      showRenameFolderDialog: false,
      showChoosePracticeDialog: false,
      editFileId: null,
      editFileVersion: 0,
      editFileName: '',
      editFileLocked: false,
      editFolderId: null,
      editFolderVersion: 0,
      editFolderName: '',
      currentFilter: false,
      savedFilter: false,
      newFileDialogKey: 0,
      dialogType: 0,
      imageSrc: false,
      imageName: false,
      dialogFile: null,
      dialogFileMlUrl: null,
      downloading: false,
      selectedFiles: [],
      fileHover: false,
      fileHoverUrl: null,
      fileHoverType: 'base64',
      fileHoverTimeout: null,
      fileHoverInterval: null,
      progressBarsValues: {},
      forceDeletionText: false,
      forceDeletionAllowed: true,
      forceDeletionInProgress: false,
      choosePracticeDialogKey: 999,
      showSetThumbnailDialog: false,
      newThumbnailFile: null,
      isLoading: false,
      showStaticMediaPicker: false,
      showMlFilePickerDialog: false,
      newThumbnailMlFile: null,
      newThumbnailStaticMedia: null,
      deleteItemId: null,
      deleteItemVersion: 0,
      itemType: null,
      showChooseParticipantDialog: false,
      showScheduleMessageDialog: false,
      newMessageData: {
        message: '',
        date: this.$moment().format('YYYY-MM-DD'),
        time: '06:00',
        resourceId: null,
        acknowledge: false,
        participants: [],
      },
      scheduleMessageResource: null,
      messageScheduled: false,
      newExerciseDialogKey: 9999,
      showEditExerciseDialog: false,
      participantsListFormType: 'scheduleMessage',
      participantsListSubmitText: 'Schedule Message',
      participantsListTitle: 'Assign to',
      participantsListReturnValue: 'id',
      exerciseResource: null,
      exerciseResourceAdditional: null,
      exerciseDialogMode: false,
      exerciseDialogDisableCancel: false,
      exerciseDialogHidePrev: false,
      newExerciseFromMl: null,
      newExerciseFromMlCollect: false,
      newExerciseFromMlFile: null,
      sendMessageMlFile: null,
      sendMessageText: '',
      customMediaThumbnailAllowedFileTypes: [18],
      thumbnailPickerKey: 0,
      thumbnailType: null,
    }
  },
  computed: {
    ...mapState('libraryStates', [
      'loading',
      'directory',
      'modalDirectory',
      'item',
    ]),
    ...mapState('userStates', ['mainFolderId', 'profileId']),
    ...mapState('resourcesStates', ['resource']),
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    ...mapState('exercisesStates', {
      exerciseDefinition: 'exerciseDefinition',
      exerciseDefinitions: 'exerciseDefinitions',
      dialogExerciseDefinitions: 'dialogExerciseDefinitions',
    }),
    currentDirectoryIsParticipantDir() {
      return this.directory.participantSharedFolder
    },
    folders() {
      if (this.directory.entries && Array.isArray(this.directory.entries))
        return this.directory.entries.filter(x => x.entryTypeId == 12)
      else return []
    },
    pagination() {
      return (
        this.directory?.pageMetadata || {
          pageNumber: 0,
          pageSize: 50,
          totalPages: 0,
          totalElements: 0,
        }
      )
    },
    files() {
      if (this.directory.entries && Array.isArray(this.directory.entries))
        return this.directory.entries.filter(x => {
          if (x.entryTypeId == 13) {
            if (this.selectedFiles.find(el => el == x.id)) x.selected = true
            else delete x.selected
            return x
          }
        })
      else return []
    },
    breadcrumbsFormatted() {
      return this.formatBreadcrumbs()
    },
    currentSearch() {
      if (
        Array.isArray(this.currentFilter) &&
        this.currentFilter.find(item => item.key == 'name')
      )
        return this.currentFilter.find(item => item.key == 'name').value
      else return false
    },
    newThumbnailSelected() {
      return (
        this.newThumbnailFile !== null ||
        this.newThumbnailMlFile !== null ||
        this.newThumbnailStaticMedia !== null
      )
    },
    exerciseSingleDefinitions() {
      let arr = []
      this.dialogExerciseDefinitions.forEach(element => {
        if (element.type == 'EXERCISE') {
          let obj = {}
          for (const [key, value] of Object.entries(element.exercise)) {
            obj[key] = value
          }
          arr.push(obj)
        }
      })
      return arr
    },
    currentFile() {
      if (
        !this.newThumbnailFile &&
        !this.newThumbnailStaticMedia &&
        !this.newThumbnailMlFile
      ) {
        return null
      }

      if (this.newThumbnailStaticMedia) {
        return {
          fileTypeId: 18,
          thumbnailUrl: this.newThumbnailStaticMedia.mediaUrl,
          mediaUrl: this.newThumbnailStaticMedia.mediaUrl,
        }
      }

      if (this.newThumbnailMlFile) {
        return {
          fileTypeId: this.newThumbnailMlFile.fileTypeId,
          thumbnailUrl: this.newThumbnailMlFile.thumbnailUrl,
          mediaUrl:
            this.newThumbnailMlFile.fileUrl || this.newThumbnailMlFile.mediaUrl,
        }
      }

      if (this.newThumbnailFile) {
        return {
          fileTypeId: this.newThumbnailFile.fileTypeId,
          thumbnailUrl: this.newThumbnailFile.thumbnailUrl,
          mediaUrl:
            this.newThumbnailFile.fileUrl || this.newThumbnailFile.mediaUrl,
        }
      }

      return null
    },
  },
  created() {
    const folderIdInHash = this.getHashNumber(window.location.hash)
    const folderId = this.$route.params?.folderId || folderIdInHash
    this.loadDirectory(folderId || this.mainFolderId)
  },
  mounted() {},
  watch: {
    $route(newRoute, oldRoute) {
      if (
        newRoute.params?.folderId !== undefined &&
        oldRoute.params?.folderId !== newRoute.params?.folderId
      ) {
        this.loadDirectory(newRoute.params.folderId)
      }

      if (this.getHashNumber(newRoute?.hash) === null) {
        this.loadDirectory(this.mainFolderId)
      }
    },
  },
  methods: {
    isStringBase64,
    getHashNumber(hash = '') {
      const folderIdHashString = hash.replace('#', '').trim()
      const folderIdInHash =
        folderIdHashString !== '' ? parseInt(folderIdHashString, 10) : null

      return folderIdInHash
    },
    ...mapActions('libraryStates', [
      'getDirectory',
      'getModalDirectory',
      'addDirectory',
      'deleteDirectory',
      'changeDirectory',
      'getFile',
      'addFilePromise',
      'deleteFile',
      'changeFile',
      'tagFile',
      'search',
      'checkFileAvailable',
      'scheduleMessagesFromLibrary',
      'uploadFile',
      'deleteFilesFromDirectory',
    ]),
    ...mapActions('resourcesStates', [
      'likeResource',
      'getResource',
      'getResourceById',
      'shareResource',
      'sendExistingResource',
      'changeResourceThumbnail',
      'changeResourceThumbnailMl',
    ]),
    ...mapActions('exercisesStates', [
      'createExercisesFromML',
      'createActivitiesFromML',
      'getExerciseDefinitions',
      'getDialogExerciseDefinitions',
    ]),
    ...mapMutations('appStates', ['setSnackbarNotification']),
    ...mapActions('chatStates', ['createChatBulk', 'bulkAddChatMessageFromMl']),
    ...mapMutations('exercisesStates', ['setExerciseDefinition']),
    searchClear() {
      this.currentFilter = false
      this.savedFilter = false
      this.loadDirectory(this.mainFolderId)
    },
    searchInput({ key, value }) {
      const oldFilters = this.savedFilter
        ? this.savedFilter.filter(el => el.key !== key)
        : []
      this.filterBy([...oldFilters, { key: key, value: value }])
    },
    searchRun(test) {
      this.filterBy(test)
    },
    openNewFileDialog() {
      this.showNewFileDialog = true
    },
    closeNewFileDialog() {
      this.showNewFileDialog = false
      this.newFileDialogKey++
    },
    openNewFolderDialog() {
      this.showNewFolderDialog = true
    },
    closeNewFolderDialog() {
      this.showNewFolderDialog = false
      this.newFolderName = ''
      this.clearNewThumbnail()
    },
    confirmNewFolder() {
      debounce(
        () =>
          this.addDirectory({
            id: this.currentDirectoryId,
            name: this.newFolderName,
          }).then(x => {
            this.showNewFolderDialog = false
            this.newFolderName = ''
          }),
        500,
        'newFolder',
      )
    },
    deleteFolderHandler() {
      if (
        this.deleteItemId &&
        (this.deleteItemVersion || this.deleteItemVersion === 0)
      )
        debounce(
          () =>
            this.deleteDirectory({
              id: this.deleteItemId,
              version: this.deleteItemVersion,
              parentId: this.currentDirectoryId,
              force: this.forceDeletionText ? true : false,
              callback: this.forceDeletePopup,
            }).then(file => {
              this.closeDialogs()
            }),
          500,
          'deleteFolder',
        )
    },
    loadDirectory(id, loader = true, pageNumber = 0, pageSize = 50) {
      this.getDirectory({ id: id, loader: loader, pageNumber, pageSize }).then(
        url => {
          this.currentDirectoryId = id
          window.location.hash = `${id}`
        },
      )
    },
    changeLibraryPage(pageNumber) {
      this.loadDirectory(
        this.directory.id,
        true,
        pageNumber - 1,
        this.pagination.pageSize,
      )
    },
    loadModalDirectory(id, actionType) {
      if (!id) id = this.currentDirectoryId
      this.getModalDirectory({ id: id })
    },
    renameFolder() {
      debounce(
        () =>
          this.changeDirectory({
            directoryId: this.editFolderId,
            parentId: this.currentDirectoryId,
            key: 'name',
            value: this.newFolderName,
            version: this.editFolderVersion,
          }).then(folder => {
            this.closeDialogs()
            this.clearEditFolderInfo()
          }),
        500,
        'renameFolder',
      )
    },
    moveFolder(id, title, version, destinationId) {
      debounce(
        () =>
          this.changeDirectory({
            directoryId: id,
            parentId: this.currentDirectoryId,
            key: 'moveDestinationId',
            value: destinationId,
            version: version,
            message: 'Directory moved',
          }),
        500,
        'moveFolder',
      )
    },
    copyFolder(id, title, version, destinationId) {
      debounce(
        () =>
          this.changeDirectory({
            directoryId: id,
            parentId: this.currentDirectoryId,
            key: 'copyToDestinationId',
            value: destinationId,
            version: version,
            message: 'Directory copied',
          }),
        500,
        'copyFolder',
      )
    },
    deleteContent(id) {
      debounce(
        () =>
          this.deleteFilesFromDirectory({
            directoryId: id,
          }),
        500,
        'deleteContent',
      )
    },
    uploadNewFile(event) {
      this.newFile = event
    },
    async addNewFile() {
      const uploadData = {
        file: this.newFile.file,
        compress: true,
        notify: this.newFile.notify === true,
      }

      if (this.newFile.title) uploadData.title = this.newFile.title
      if (this.newFile.description)
        uploadData.description = this.newFile.description

      // Add uploading task
      await this.addUploadingTask(
        this.newFile.title,
        this.addFilePromise,
        {
          directoryId: this.currentDirectoryId,
          data: uploadData,
        },
        () => {
          this.refreshCurrentFolder()
        },
      )
    },
    refreshCurrentFolder() {
      this.loadDirectory(
        this.currentDirectoryId,
        false,
        this.pagination?.pageNumber,
        this.pagination?.pageSize,
      )
    },
    openCreateExerciseDialog(file) {
      this.participantsListFormType = 'createExercise'
      this.openExerciseDialog(file, 'EXERCISE', false, true)
    },
    openCreateActivityDialog(file) {
      this.participantsListFormType = 'createActivity'
      this.openExerciseDialog(file, 'ACTIVITY', false, true)
    },
    openExerciseDialog(file, mode, disableCancel, hidePrev) {
      this.exerciseResource = {
        id: file.id,
        previewUrl: file.previewUrl,
        thumbnailUrl: file.thumbnailUrl,
        name: file.name,
        title: file.title,
        dialogFileMlUrl: file.fileUrl,
        fileUrl: file.fileUrl,
        fileTypeId: file.fileTypeId,
      }

      if (file.thumbnailUrl) {
        this.exerciseResourceAdditional = {
          id: file.id,
          previewUrl: file.previewUrl,
          thumbnailUrl: file.thumbnailUrl,
          name: file.name,
          title: file.title,
          dialogFileMlUrl: file.fileUrl,
          fileUrl: file.fileUrl,
          fileTypeId: file.fileTypeId,
        }
      }

      if (mode) this.exerciseDialogMode = mode
      if (disableCancel) this.exerciseDialogDisableCancel = true
      if (hidePrev) this.exerciseDialogHidePrev = true
      this.setExerciseDefinition(false)
      this.showEditExerciseDialog = true
    },
    openChooseParticipantDialog(type) {
      if (!type) return
      this.participantsListFormType = type
      this.participantsListTitle = 'Assign to'
      this.participantsListReturnValue = 'id'
      if (this.participantsListFormType === 'scheduleMessage') {
        this.participantsListSubmitText = 'Schedule Message'
      } else if (this.participantsListFormType === 'createExercise') {
        this.participantsListSubmitText = 'Create Exercise'
      } else if (this.participantsListFormType === 'createActivity') {
        this.participantsListSubmitText = this.tenantTexts.library.createActivity
      } else if (this.participantsListFormType === 'sendMessage') {
        this.participantsListSubmitText = 'Send Message'
        this.participantsListTitle = 'Send Message'
        this.participantsListReturnValue = null
      } else {
        this.participantsListSubmitText = 'Choose participants'
      }
      this.showChooseParticipantDialog = true
    },
    closeChooseParticipantDialog() {
      this.showChooseParticipantDialog = false
    },
    async openSendMessageDialog(file) {
      if (file?.id) {
        this.sendMessageMlFile = file
        this.showBiggerImage({
          url: null,
          file: null,
          name: null,
          mlResourceUrl: null,
        }) // Show dialog while getting new s3 file url

        this.getResourceById({ resourceId: file.id })
          .then(response => {
            const { data } = response
            let type = 2
            switch (data.fileTypeId) {
              case 18:
                type = 2
                break
              case 19:
                type = 4
                break
              case 20:
                type = 6
                break
              default:
                type = 2
                break
            }

            if (data.fileUrl) {
              this.showBiggerImage({
                url: data.fileUrl,
                file: data.previewUrl,
                name: data.name,
                mlResourceUrl: file.id,
                type,
              })

              this.participantsListFormType = 'sendMessage'
            }
          })
          .catch(error => {
            console.log(error)
          })
      }
      // show modal sending to chat
    },
    openScheduleMessageDialog(id, files) {
      this.showScheduleMessageDialog = true
      this.newMessageData.resourceId = id
      this.scheduleMessageResource = files.find(el => el.id === id) || null
    },
    closeScheduleMessageDialog() {
      this.showScheduleMessageDialog = false
      this.newMessageData = {
        resourceId: null,
        message: '',
        date: this.$moment().format('YYYY-MM-DD'),
        time: '06:00',
        acknowledge: false,
        participants: [],
      }
      this.scheduleMessageResource = null
    },
    openConfirmDeleteDialog(id, version, type) {
      this.itemType = type
      this.deleteItemId = id
      this.deleteItemVersion = version
      this.showConfirmDeleteDialog = true
    },
    openDeleteFileDialog() {
      this.showConfirmDeleteDialog = false
      this.showDeleteFileDialog = true
    },
    openRenameFileDialog(id, version, name, locked) {
      this.editFileId = id
      this.editFileVersion = version
      this.editFileName = name
      this.editFileLocked = locked
      this.changeFileName = name
      this.showRenameFileDialog = true
    },
    openDeleteFolderDialog() {
      this.showConfirmDeleteDialog = false
      this.showDeleteFolderDialog = true
    },
    openRenameFolderDialog(id, version, name) {
      this.editFolderId = id
      this.editFolderVersion = version
      this.editFolderName = name
      this.newFolderName = name
      this.showRenameFolderDialog = true
    },
    closeDialogs() {
      this.showDeleteFileDialog = false
      this.showRenameFileDialog = false
      this.showDeleteFolderDialog = false
      this.showRenameFolderDialog = false
      this.showSetThumbnailDialog = false
      this.showConfirmDeleteDialog = false
      this.editFileName = null
      this.editFolderId = null
      this.editFolderVersion = null
      this.editFolderName = null
      this.newFolderName = ''
      this.changeFileName = ''
      this.forceDeletionText = false
      this.forceDeletionAllowed = true
      this.forceDeletionInProgress = false
      this.clearNewThumbnail()
      this.showChooseParticipantDialog = false
      this.showScheduleMessageDialog = false
      this.newMessageData = {
        resourceId: null,
        message: '',
        date: this.$moment().format('YYYY-MM-DD'),
        time: '06:00',
        acknowledge: false,
        participants: [],
      }
      this.scheduleMessageResource = null
    },
    clearEditFileInfo() {
      this.editFileId = null
      this.editFileVersion = null
      this.newThumbnailFile = null
    },
    clearEditFolderInfo() {
      this.editFolderId = null
      this.editFolderVersion = null
    },
    deleteFileHandler() {
      this.forceDeletionInProgress = true
      if (this.deleteItemId)
        debounce(
          () =>
            this.deleteFile({
              directoryId: this.currentDirectoryId,
              itemId: this.deleteItemId,
              version: this.deleteItemVersion,
              force: this.forceDeletionText ? true : false,
              callback: this.forceDeletePopup,
            }).then(file => {
              this.closeDialogs()
            }),
          500,
          'deleteFile',
        )
    },
    async forceDeletePopup(response) {
      setTimeout(() => {
        this.showDeleteFileDialog = true
        this.forceDeletionText = response.explanations
        this.forceDeletionAllowed = response.allowed
        this.forceDeletionInProgress = false
      }, 1000)
    },
    selectFile(id) {
      if (this.selectedFiles.find(el => el === id))
        this.selectedFiles.splice(this.selectedFiles.indexOf(id), 1)
      else this.selectedFiles.push(id)
    },
    editFile(id, title, key, value, version, message = false) {
      debounce(
        () =>
          this.changeFile({
            directoryId: this.currentDirectoryId,
            itemId: id,
            title: title,
            key: key,
            value: value,
            version: version,
            message: message,
          }).then(file => {
            this.closeDialogs()
            this.clearEditFileInfo()
          }),
        500,
        'editFile',
      )
    },
    favoriteResource(id, value) {
      debounce(
        () =>
          this.likeResource({ resourceId: id, isFavorite: value }).then(() => {
            if (!this.currentFilter)
              this.loadDirectory(this.currentDirectoryId, false)
            else {
              this.filterBy(this.savedFilter)
            }
          }),
        500,
        'favoriteResource',
      )
    },
    toggleTag(id, version, labels, value) {
      debounce(
        () =>
          this.tagFile({
            directoryId: this.currentDirectoryId,
            itemId: id,
            version: version,
            labels: labels,
            value: value,
          }).then(() => {
            if (!this.currentFilter)
              this.loadDirectory(this.currentDirectoryId, false)
            else {
              this.filterBy(this.savedFilter)
            }
          }),
        500,
        'tagFile',
      )
    },
    renameFile() {
      const editFileId = this.editFileId
      const changeFileName = this.changeFileName
      const editFileVersion = this.editFileVersion
      this.closeDialogs()
      debounce(
        () =>
          this.editFile(
            editFileId,
            changeFileName,
            'title',
            changeFileName,
            editFileVersion,
          ),
        100,
        'editFile',
      )
    },
    moveFile(id, title, version, destinationId) {
      debounce(
        () =>
          this.editFile(
            id,
            title,
            'moveDestinationId',
            destinationId,
            version,
            'File moved',
          ),
        100,
        'moveFile',
      )
    },
    copyFile(id, title, version, destinationId) {
      debounce(
        () =>
          this.editFile(
            id,
            title,
            'copyToDestinationId',
            destinationId,
            version,
            'File copied',
          ),
        100,
        'copyFile',
      )
    },
    formatBreadcrumbs() {
      //transform breadcrumbs from nested breadcrumb object (content in content in content...) to simple array
      let items = []
      let b = this.directory.breadcrumb
      if (b) rec(b, true)
      function rec(b, root = false) {
        let item = {}
        item.name = b.name
        item.id = b.id
        if (root) item.root = root
        items.push(item)
        if (b.content && b.content.length > 0) {
          rec(b.content[0])
        }
      }
      return items
    },
    filterBy(filters) {
      let filtersArray = []
      let tmpObj = {}
      if (Array.isArray(filters)) {
        filtersArray = filters
      } else if (typeof filters === 'object' && filters.key) {
        filtersArray = [filters]
      } else {
        filtersArray = false
      }

      if (Array.isArray(filtersArray)) {
        filtersArray.forEach(filter => {
          tmpObj[filter.key] = filter.value
        })
      } else tmpObj = false
      if (tmpObj && Object.keys(tmpObj).length > 0) {
        this.search({
          directoryId: this.mainFolderId,
          params: this.requestQuery(tmpObj),
        }).then(url => {
          this.currentFilter = filtersArray
          this.savedFilter = filtersArray
        })
      } else {
        this.loadDirectory(this.mainFolderId)
        this.currentFilter = false
        this.savedFilter = false
      }
    },
    requestQuery(filter) {
      let keys = Object.keys(filter).filter(key => filter[key] || filter[key])
      return keys.reduce((acc, key) => {
        acc.append(key, filter[key])
        return acc
      }, new URLSearchParams())
    },
    async downloadFile(file, retry = true) {
      await this.getFile(file)
      let callback = this.checkFileCallback
      if (!retry) callback = false
      this.checkFileAvailable({
        url: this.item.fileUrl,
        directoryId: this.currentDirectoryId,
        itemId: this.item.id,
        name: this.item.name,
        cb: callback,
      })
    },
    downloadFileHandler(retry = true, id = null) {
      this.downloading = true
      let callback = this.checkFileCallback
      if (!retry) callback = false
      if (this.imageSrc)
        this.checkFileAvailable({
          url: this.imageSrc,
          dialogFileMlUrl: this.dialogFileMlUrl,
          name: this.name,
          cb: callback,
        })
      else {
        if (id)
          this.downloadFile(
            { directoryId: this.currentDirectoryId, itemId: id },
            true,
          )
        this.downloading = false
      }
    },
    checkFileCallback(resourceUrl) {
      this.downloading = false
      if (this.imageSrc) {
        this.reloadImageResource(resourceUrl)
        // try once 5s after error
        setTimeout(() => {
          this.downloadFileHandler(false, resourceUrl)
        }, 5000)
      } else {
        setTimeout(() => {
          this.downloadFile(
            { directoryId: this.currentDirectoryId, itemId: resourceUrl },
            false,
          )
        }, 5000)
      }
    },
    showBiggerImage({ url, file, name, mlResourceUrl = null, type = 1 }) {
      this.imageSrc = url
      this.dialogFile = file
      this.imageName = name
      this.dialogFileMlUrl = mlResourceUrl
      this.dialogType = type
    },
    async reloadImageResource(id) {
      await this.getResource({ id: id })
      this.imageSrc = this.resource.fileUrl
      this.imageName = this.resource.name
    },
    closeMediaDialog() {
      this.dialogType = 0
      this.imageName = null
      this.currentFile = null
      this.imageSrc = null
      this.dialogFileMlUrl = null
    },
    fileOpenHover(id, thumbnailUrl = false, previewUrl = false) {
      clearTimeout(this.fileHoverTimeout)
      this.fileCloseHover()
      if (id && thumbnailUrl) {
        this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 1 } }
        if (this.fileHoverInterval) clearInterval(this.fileHoverInterval)
        this.fileHoverInterval = setInterval(() => {
          if (this.progressBarsValues[id] >= 100) {
            clearInterval(this.fileHoverInterval)
            return
          }
          let tmp = this.progressBarsValues[id]
          this.progressBarsValues = {
            ...this.progressBarsValues,
            ...{ [id]: tmp + 33 },
          }
        }, 200)
        if (this.fileHoverTimeout) clearTimeout(this.fileHoverTimeout)
        this.fileHoverTimeout = setTimeout(() => {
          this.fileHover = null
          if (previewUrl) {
            this.fileHoverUrl = previewUrl // instead of base64 in response new files use now s3 url to get preview
            this.fileHoverType = this.isStringBase64(previewUrl)
              ? 'base64'
              : 'url'
            this.fileHover = true
          } else {
            this.getResourceById({ resourceId: id }).then(response => {
              const { data } = response
              if (data.fileUrl) {
                if (this.fileHover === null) {
                  this.fileHoverUrl = data.previewUrl || data.thumbnailUrl // in case preview is not available
                  this.fileHoverType = this.isStringBase64(
                    data.previewUrl || data.thumbnailUrl,
                  )
                    ? 'base64'
                    : 'url'
                  this.fileHover = true
                }
              }
            })
          }
        }, 1000)
      } else this.fileClearHover(id)
    },
    fileClearHover(id) {
      this.progressBarsValues = { ...this.progressBarsValues, ...{ [id]: 0 } }

      clearInterval(this.fileHoverInterval)
      if (!this.fileHover) {
        clearTimeout(this.fileHoverTimeout)
        this.fileHover = false
        this.fileHoverUrl = null
        this.fileHoverType = 'base64'
      }
    },
    fileCloseHover() {
      this.fileHover = false
    },
    openChoosePracticeDialog(id) {
      this.editFileId = id
      this.showChoosePracticeDialog = true
    },
    closeChoosePracticeDialog() {
      this.showChoosePracticeDialog = false
      this.choosePracticeDialogKey++
    },
    choosePractice(practices) {
      this.shareResource({
        resourceId: this.editFileId,
        companyId: practices[0],
      }).then(() => {
        this.editFileId = null
      })
    },
    openSetThumbnailDialog(id, version) {
      this.clearNewThumbnail()

      this.getResourceById({ resourceId: id }).then(response => {
        const { data } = response
        this.newThumbnailFile = data
        this.newThumbnailMlFile = data
        this.newThumbnailStaticMedia = data
      })

      this.editFileId = id
      this.editFileVersion = version
      this.showSetThumbnailDialog = true
    },
    setThumbnailFile(thumbnail = null) {
      if (!thumbnail) {
        this.newThumbnailFile = null
        this.newThumbnailStaticMedia = null
        this.newThumbnailMlFile = null
        this.thumbnailType = null
        return
      }

      const { type, file } = thumbnail

      this.thumbnailType = type

      if (type === 'file-picker') {
        this.newThumbnailFile = file
        this.newThumbnailMlFile = null
        this.newThumbnailStaticMedia = null
      } else if (type === 'mobile-static-media') {
        this.newThumbnailStaticMedia = file
        this.newThumbnailFile = null
        this.newThumbnailMlFile = null
      } else if (type === 'media-library') {
        this.newThumbnailMlFile = file
        this.newThumbnailFile = null
        this.newThumbnailStaticMedia = null
      }
    },
    async changeFileThumbnail() {
      if (this.thumbnailType === 'file-picker' && this.newThumbnailFile) {
        let storageFileId

        try {
          const fileResponse = await this.uploadFile({
            file: this.newThumbnailFile.file,
          })
          storageFileId = fileResponse.data.id
        } catch (error) {
          console.error(error)
        }

        if (!storageFileId) return

        this.changeResourceThumbnail({
          resourceId: this.editFileId,
          storageFileId,
        }).then(() => {
          this.closeDialogs()
          this.clearEditFileInfo()
          this.loadDirectory(
            this.currentDirectoryId,
            false,
            this.pagination?.pageNumber,
            this.pagination?.pageSize,
          )
        })
      } else {
        this.changeResourceThumbnailMl({
          resourceId: this.editFileId,
          version: this.editFileVersion,
          mlFileId: this.newThumbnailMlFile?.id,
          staticMediaId: this.newThumbnailStaticMedia?.id,
        }).then(() => {
          this.closeDialogs()
          this.clearEditFileInfo()
          this.loadDirectory(
            this.currentDirectoryId,
            false,
            this.pagination?.pageNumber,
            this.pagination?.pageSize,
          )
        })
      }
    },
    convertImageToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },
    onClickOutsideFileHover() {
      this.fileCloseHover()
    },
    onKeydownFileHover(key) {
      if (key.code === 'Escape' && key.isTrusted) this.onClickOutsideFileHover()
    },
    onClickOutsideNewFolder() {
      this.closeNewFolderDialog()
    },
    onKeydownNewFolder(key) {
      if (key.code === 'Escape' && key.isTrusted) this.onClickOutsideNewFolder()
    },
    onClickOutsideDeleteFile() {
      this.closeDialogs()
      this.clearEditFileInfo()
      this.clearEditFolderInfo()
    },
    onKeydownDeleteFile(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideDeleteFile()
    },
    onClickOutsideReneameFile() {
      this.closeDialogs()
      this.clearEditFileInfo()
    },
    onKeydownReneameFile(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideReneameFile()
    },
    onClickOutsideDeleteFolder() {
      this.closeDialogs()
      this.clearEditFileInfo()
    },
    onKeydownDeleteFolder(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideDeleteFolder()
    },
    onClickOutsideRenameFolder() {
      this.closeDialogs()
    },
    onKeydownRenameFolder(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideRenameFolder()
    },
    onClickOutsideSetThumbnail() {
      this.closeDialogs()
    },
    onKeydownSetThumbnail(key) {
      if (key.code === 'Escape' && key.isTrusted)
        this.onClickOutsideSetThumbnail()
    },

    /* new thumbnail */
    openStaticMediaPicker() {
      this.showStaticMediaPicker = true
    },
    closeStaticMediaPicker() {
      this.showStaticMediaPicker = false
    },
    chosenStaticMediaPicker(staticMedia = null) {
      this.closeStaticMediaPicker()

      if (staticMedia === null) return
      this.newThumbnailStaticMedia = staticMedia
      this.setThumbnailFile({ type: 'mobile-static-media', file: staticMedia })
    },
    openMlFilePickerDialog() {
      this.showMlFilePickerDialog = true
    },
    closeMlFilePickerDialog() {
      this.showMlFilePickerDialog = false
    },
    chosenFileMlFilePickerDialog(id, _, file) {
      this.closeMlFilePickerDialog()
      if (file === null || file === undefined) {
        this.newThumbnailMlFile = null
        this.setThumbnailFile(null)
      } else {
        this.newThumbnailMlFile = {
          ...file,
          id,
        }
        this.setThumbnailFile({
          type: 'media-library',
          file: this.newThumbnailMlFile,
        })
      }
    },
    clearNewThumbnail() {
      this.newThumbnailFile = null
      this.newThumbnailMlFile = null
      this.newThumbnailStaticMedia = null
      this.thumbnailType = null
      this.thumbnailPickerKey++
    },
    reloadThumbnailResource() {
      if (this.editFileId) {
        this.getResourceById({ resourceId: this.editFileId }).then(response => {
          const { data } = response
          this.newThumbnailFile = data
          this.newThumbnailMlFile = data
          this.newThumbnailStaticMedia = data
        })
      }
    },
    onSubmitParticipantsList(participants) {
      if (this.participantsListFormType === 'scheduleMessage') {
        this.onScheduleMessage(participants)
      } else if (this.participantsListFormType === 'createExercise') {
        this.createExercisesFromML({
          data: this.newExerciseFromMl,
          mlFile: this.newExerciseFromMlFile,
          participants,
          collect: this.newExerciseFromMlCollect,
        })
      } else if (this.participantsListFormType === 'createActivity') {
        this.createActivitiesFromML({
          data: this.newExerciseFromMl,
          mlFile: this.newExerciseFromMlFile,
          participants,
          collect: this.newExerciseFromMlCollect,
        })
      } else if (this.participantsListFormType === 'sendMessage') {
        this.submitSendMessage({
          mlFile: this.sendMessageMlFile,
          participants,
        })
      }
    },
    onScheduleMessage(participants) {
      this.newMessageData.participants = participants
      const scheduleMessage = {
        message: this.newMessageData.message,
        date: this.newMessageData.date,
        time: this.newMessageData.time,
        resourceId: this.newMessageData.resourceId,
        acknowledge: this.newMessageData.acknowledge,
        participants: participants,
      }
      this.scheduleMessagesFromLibrary(scheduleMessage)
      this.messageScheduled = true
      this.closeDialogs()
    },
    reloadExerciseResource(id, type) {
      this.prepareExerciseResource(id, type)
    },
    prepareExerciseResource(id, resourceType) {
      if (!id) {
        if (resourceType == 'additionalMedia') {
          this.exerciseResourceAdditional = null
        } else {
          this.exerciseResource = null
        }
      } else {
        this.getResourceById({ resourceId: id })
          .then(response => {
            const { data } = response
            let type = 2
            switch (data.fileTypeId) {
              case 18:
                type = 2
                break
              case 19:
                type = 4
                break
              default:
                type = 2
                break
            }
            if (resourceType == 'additionalMedia') {
              if (data.fileUrl) {
                this.exerciseResourceAdditional = {
                  id: id,
                  previewUrl: data.previewUrl,
                  thumbnailUrl: data.thumbnailUrl,
                  name: data.name,
                  title: data.title,
                  dialogFileMlUrl: data.fileUrl,
                  fileUrl: data.fileUrl,
                  fileTypeId: data.fileTypeId,
                }
              } else {
                this.exerciseResourceAdditional = null
              }
            } else {
              if (data.fileUrl) {
                this.exerciseResource = {
                  id: id,
                  previewUrl: data.previewUrl,
                  thumbnailUrl: data.thumbnailUrl,
                  name: data.name,
                  title: data.title,
                  dialogFileMlUrl: data.fileUrl,
                  fileUrl: data.fileUrl,
                  fileTypeId: data.fileTypeId,
                }
              } else {
                this.exerciseResource = null
              }
            }
          })
          .catch(error => {
            console.log(error)
          })
      }
    },
    clearExerciseResource() {
      this.exerciseResource = null
    },
    clearExerciseResourceAdditional() {
      this.exerciseResourceAdditional = null
    },
    reloadExerciseDefinitions(type) {
      let params = { params: { folder: 'All' } }
      if (type) params.params.type = type
      this.getExerciseDefinitions(params)
    },
    loadDialogExerciseDefinitions(params) {
      let defaultParams = {
        type: 'EXERCISE',
        page: 0,
        pageSize: 50,
        sortBy: 'creationDate',
      }
      this.getDialogExerciseDefinitions({
        params: { ...defaultParams, ...params },
      })
    },
    createNewExerciseDefinition(data, addToCollection = false) {
      data.date = this.$moment().format('YYYY-MM-DD')
      this.newExerciseFromMl = data
      this.newExerciseFromMlCollect = addToCollection
      this.newExerciseFromMlFile = this.exerciseResource
      this.openChooseParticipantDialog(this.participantsListFormType)
    },
    sendMessageAdd() {
      if (!this.sendMessageText && !this.currentFile && !this.imageSrc) {
        return
      }

      this.openChooseParticipantDialog(this.participantsListFormType)

      this.showBiggerImage({
        url: null,
        file: null,
        name: null,
        mlResourceUrl: null,
        type: 0,
      })
    },
    async submitSendMessage({ mlFile = null, participants = [] }) {
      if (!participants || participants.length === 0 || !mlFile) return

      try {
        const response = await this.createChatBulk({ participants })
        const channels = response.data

        const profileIds = participants.map(el => el.profileId)
        const sentResourceResponse = await this.sendExistingResource({
          resourceId: mlFile.id,
          receiversIds: profileIds,
        })
        const sentResource = sentResourceResponse.data

        const successChannels = await this.bulkAddChatMessageFromMl({
          messageText: this.sendMessageText,
          mediaLibraryFiles: sentResource,
          channels,
        })
        const failedParticipantsUuid = channels
          .filter(el => !successChannels.find(ch => ch.uuid === el.uuid))
          .map(el => el.uuid)
        if (failedParticipantsUuid.length) {
          const failedParticipants = participants.filter(el =>
            failedParticipantsUuid.includes(el.uuid),
          )
          let names = ''

          for (const participant of failedParticipants) {
            if (names !== '') names += ', '
            names += `${participant.firstName} ${participant.lastName}`
          }

          this.setSnackbarNotification(
            'Message sent partially! Failed to: ' + names,
          )
        } else {
          this.setSnackbarNotification('Message sent!')
        }
        this.sendMessageEdit('')
      } catch (error) {
        console.error(error)
      }
    },
    expandMessageInput(reset = false) {
      this.$nextTick(() => {
        const textField = this.$refs.textField.$refs.input
        if (reset) {
          textField.style.height = '20px'
        } else {
          textField.style.height = '5px'
          textField.style.height = textField.scrollHeight + 'px'
        }
      })
    },
    sendMessageEdit(message) {
      this.sendMessageText = message
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.library {
  background: $bh-white;
  padding: 0 2 * $gutter;
  height: 100%;
  &-breadcrumbs {
  }
  &-heading {
    color: $bh-gray--2;
    font-size: 1rem;
    padding-left: 8px;
  }
  &-folders {
    display: flex;
    flex-wrap: wrap;
    &__new {
      width: 160px;
      min-height: 40px;
      border-radius: 8px;
      margin: 8px;
      display: flex;
      align-items: center;
      padding: 4px 0 4px 8px;
      background: $bh-white;
      border: 1px solid $bh-blue--4;
      -webkit-transition-duration: 150ms;
      -moz-transition-duration: 150ms;
      -ms-transition-duration: 150ms;
      -o-transition-duration: 150ms;
      transition-duration: 150ms;
      cursor: pointer;
      color: $bh-blue;
      font-weight: bold;
      .v-icon {
        width: 24px;
        height: 24px;
        margin-right: 12px;
        background: $bh-blue--4;
        border-radius: 6px;
        font-size: 12px;
        color: $bh-blue;
        text-align: center;
      }
      &:hover {
        .v-icon {
          background: $bh-gray--6;
        }
      }
    }
  }
  &-files {
    display: flex;
    flex-wrap: wrap;
    &__new {
      width: 161px;
      height: 220px;
      border-radius: 8px;
      border: 1px solid $bh-blue--4;
      margin: 8px;
      -webkit-transition-duration: 150ms;
      -moz-transition-duration: 150ms;
      -ms-transition-duration: 150ms;
      -o-transition-duration: 150ms;
      transition-duration: 150ms;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      text-align: center;
      color: $bh-blue;
      font-weight: bold;
      font-size: 1rem;
      .v-icon {
        width: 48px;
        height: 48px;
        margin-top: 49px;
        margin-bottom: 70px;
        border-radius: 6px;
        background: $bh-blue--4;
        color: $bh-blue;
      }
      &:hover {
        .v-icon {
          background: $bh-gray--6;
        }
      }
    }
  }
  .file-hover-dialog {
    width: auto !important;
    padding: 10px;
    background: $bh-white;
    .v-btn {
      position: absolute;
      top: 10px;
      right: 10px;
      width: 32px;
      height: 32px;
      background: $bh-blue;
    }
    img {
      display: block;
      max-height: 80vh;
      max-width: 1000px;
    }
  }
  .library-pagination {
    min-height: 100px;

    .v-pagination__item {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
}
.v-card__title {
  word-break: normal;
}
.add-media-menu__list {
  .upload-file {
    &,
    .v-text-field > .v-input__control > .v-input__slot {
      cursor: pointer;
    }

    .v-input__prepend-inner .v-icon,
    .v-file-input__text {
      color: $bh-blue !important;
    }
    .v-input__control > .v-input__slot {
      padding: 0 !important;
      background-color: transparent !important;
    }
  }
  .v-btn {
    flex: 1;
  }
  .v-btn__content {
    justify-content: flex-start;
  }
}
.new-schedule-message {
  &-dialog {
    height: 500px;
    max-height: calc(100% - #{2 * $gutter}) !important;
    // looks little hacky
    > .v-card {
      width: 100%;
      height: 100%;
    }
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-file-details {
    display: inline-block;
    img {
      width: 40px;
      height: 40px;
      display: inline-block;
      vertical-align: middle;
      object-fit: cover;
      margin-left: 10px;
      cursor: pointer;
    }
    .v-progress-circular {
      margin-left: 10px;
    }
    span {
      cursor: default;
      margin-left: 5px;
    }
  }
  &-form {
    background-color: $bh-blue--4;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: auto;

    &-body {
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
}
</style>
